import { defineStore } from 'pinia';
import { ref, toRaw } from 'vue';
import type { MeasurementListRow } from '@pages/measurement-lists/measurement-list-row.vue';

import { CACHE_TTL, onTabResume } from '@/store/cache-ttl';
import { entityCache, keyedLruCache } from '@/store/cache-primitives';

export interface MChartViewCache {
  filterKey: string;
  mChartIds: number[];
}

// MeasurementListRow is a frontend-derived shape, so the source DTO hash alone can't
// guard it — bump this whenever the row/snapshot shape changes.
const SNAPSHOT_VERSION = 2;

// View caches are keyed per filter combination so switching filters back and forth
// restores instantly (mirrors store/article-table.ts).
const MAX_VIEW_CACHES = 20;

type MChartTableSnapshot = {
  version: number;
  rows: Record<number, MeasurementListRow>;
  viewCaches: Record<string, MChartViewCache>;
};

export const useMChartTable = defineStore('mchart-table', () => {
  const entities = entityCache<MeasurementListRow>();
  const rows = entities.data;
  const revision = ref(0);
  const lru = keyedLruCache<MChartViewCache>(MAX_VIEW_CACHES);
  const viewCaches = lru.data;

  function setViewCache(cache: MChartViewCache) {
    lru.set(cache.filterKey, cache);
  }

  // Rows are derived from MChartListItemDto, so guard the persisted snapshot with that
  // DTO's schema hash (plus SNAPSHOT_VERSION for the frontend row shape). The seeded
  // rows always revalidate on the next table mount, so stale seed data is fine. See
  // store/warm-boot.ts.
  const snapshotDto = 'MChartListItemDto' as const;

  function snapshotData(): MChartTableSnapshot {
    // toRaw: IndexedDB's structured clone can't handle Vue's reactive proxies.
    return { version: SNAPSHOT_VERSION, rows: toRaw(rows.value), viewCaches: toRaw(viewCaches.value) };
  }

  function seedSnapshot(data: unknown) {
    const snapshot = data as Partial<MChartTableSnapshot>;
    if (snapshot.version !== SNAPSHOT_VERSION) return;
    if (Object.keys(rows.value).length > 0 || Object.keys(viewCaches.value).length > 0) return;
    entities.seed(snapshot.rows ?? {});
    viewCaches.value = snapshot.viewCaches ?? {};
  }

  function cacheRows(items: MeasurementListRow[]) {
    entities.cache(items);
    revision.value++;
  }

  function update(row: MeasurementListRow) {
    entities.set(row);
    revision.value++;
  }

  function remove(id: number) {
    entities.remove([id]);
    for (const cache of Object.values(viewCaches.value)) {
      cache.mChartIds = cache.mChartIds.filter((existing) => existing !== id);
    }
    revision.value++;
  }

  function clear() {
    entities.clear();
    lru.clear();
  }

  onTabResume((elapsed) => {
    if (elapsed >= CACHE_TTL) {
      // Drop only the list caches so the next table mount fetches a fresh list; keep
      // the row DTOs so anything still referenced shows data instead of nothing.
      lru.clear();
    }
  });

  return {
    rows,
    revision,
    viewCaches,
    setViewCache,
    cacheRows,
    snapshotDto,
    snapshotData,
    seedSnapshot,
    update,
    remove,
    clear,
  };
});
